import api from "./api";
import type { AxiosRequestConfig, AxiosResponse } from "axios";

type OptionalSessionConfig = AxiosRequestConfig & {
  skipAuthRedirect?: boolean;
};

export interface StorefrontFeatures {
  freeShipping: boolean;
  easyReturns: boolean;
  securePayment: boolean;
}

export interface CompanyProfile {
  id: string;
  name: string;
  nameAr: string | null;
  slug: string;
  sectorType: string;
  phone: string | null;
  email: string;
  address: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  logoMediaId: string | null;
  faviconMediaId: string | null;
  logoMediaUrl?: string | null;
  faviconMediaUrl?: string | null;
  taxNumber: string | null;
  currencyCode: string;
  languageCode: string;
  theme: Record<string, string> | null;
  storefrontFeatures?: StorefrontFeatures;
  aiAssistantEnabled?: boolean;
  adminAiAssistantEnabled?: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface CompanyAuthResponse {
  company: CompanyProfile;
  tokens: { accessToken: string; refreshToken: string };
}

export interface UpdateCompanyProfileInput {
  name: string;
  nameAr?: string;
  sectorType?: string;
  email: string;
  phone?: string;
  address?: string;
  taxNumber?: string;
  currencyCode: string;
}

const companyService = {
  login(data: { email: string; password: string }): Promise<AxiosResponse> {
    return api.post("/companies/login", data);
  },

  logout(accessToken?: string | null): Promise<AxiosResponse> {
    return api.post("/companies/logout", undefined, {
      headers: accessToken
        ? { Authorization: `Bearer ${accessToken}` }
        : undefined,
      skipAuthRedirect: true,
    } as OptionalSessionConfig);
  },

  getProfile(config: OptionalSessionConfig = {}): Promise<AxiosResponse> {
    return api.get("/companies/profile", config);
  },

  updateProfile(data: UpdateCompanyProfileInput): Promise<AxiosResponse> {
    return api.patch("/companies/profile", data);
  },

  updateBranding(data: {
    logoMediaId?: string;
    faviconMediaId?: string;
  }): Promise<AxiosResponse> {
    return api.patch("/companies/branding", data);
  },

  updateLanguage(languageCode: string): Promise<AxiosResponse> {
    return api.patch("/companies/language", { languageCode });
  },

  updateTheme(theme: Record<string, string>): Promise<AxiosResponse> {
    return api.patch("/companies/theme", { theme });
  },

  updateStorefrontFeatures(
    features: Partial<StorefrontFeatures>,
  ): Promise<AxiosResponse> {
    return api.patch("/companies/storefront-features", { features });
  },

  updateAiAssistant(flags: {
    enabled?: boolean;
    adminEnabled?: boolean;
  }): Promise<AxiosResponse> {
    return api.patch("/companies/ai-assistant", flags);
  },
};

export default companyService;
